import React from 'react';
import theme from './AttachmentList.module.scss';
import { Trans } from '@lingui/react/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { faLock, faEyeSlash, faDownload, faTrashAlt, faExternalLink, faEye } from '@fortawesome/pro-light-svg-icons';
import { AttachmentLike, AttachmentEditMode } from '.';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OptionalId, ChangedItem, FileMetadata } from '@/types';
import SensitiveInfoTooltip from '@/components/SensitiveInfoTooltip';
import SensitiveInfoCheckbox from '@/components/SensitiveInfoCheckbox';
import AttachmentFileLink from './AttachmentFileLink';
import { FileDataPreviewLink, FileIcon, ToggleInput } from '@ygtechnologies/bygr-frontend-component-library';
import { Checkbox, Dropdown, DropdownContextMenu } from '@ygtechnologies/rubus-ui';

interface AttachmentListItemProps<T extends OptionalId<AttachmentLike> = OptionalId<AttachmentLike>> {
    att: T;
    canEdit: boolean;
    editMode?: AttachmentEditMode;
    showVisibilityFlags: boolean;
    onChange?(change: ChangedItem<T>): void;
    onDelete?(att: T): void;
    ownerProfileId: number | null | undefined;
    openUrl?: string;
    downloadUrl?: string;
}

const AttachmentListItem: React.FC<AttachmentListItemProps> = ({
    att,
    canEdit,
    editMode = 'inline',
    showVisibilityFlags,
    onChange,
    onDelete,
    ownerProfileId,
    openUrl,
    downloadUrl,
}) => {
    const handleChange = (changes: Partial<typeof att>) => {
        triggerHandler(onChange, ChangedItem.fromMerge(att, changes));
    };

    const handleDelete = () => {
        triggerHandler(onDelete, att);
    };

    const renderLink = () => {
        if (openUrl) {
            return (
                <AttachmentFileLink value={att} url={openUrl}>
                    {att.name}
                </AttachmentFileLink>
            );
        }

        return <span>{att.name}</span>;
    };

    function isEditMode(mode: AttachmentEditMode) {
        return canEdit && editMode === mode;
    }

    const dropdownItemsVisibility = {
        preview: !!att.fileData,
        download: !att.fileData && !!downloadUrl,
        open: !att.fileData && !!openUrl,
        delete: canEdit && !!onDelete,

        hideFromBuyer: isEditMode('context'),
    };

    const showContextMenu = Object.values(dropdownItemsVisibility).some((x) => x);

    return (
        <>
            <FileIcon value={att.fileMetadata ?? FileMetadata.unknown} className={theme.fileIcon} />
            <div className={theme.name}>
                <small>{att.fileMetadata?.fileType}</small>
                <div className={theme.nameContainer}>
                    {isEditMode('inline') ? (
                        <ToggleInput
                            iconMode="after"
                            wrapperClassName={theme.toggleInput}
                            value={att.name}
                            onAccept={(e) => handleChange({ name: e.target.value })}
                        />
                    ) : (
                        renderLink()
                    )}
                </div>

                {/* {showVisibilityFlags && !canEdit && (
                    <div className={theme.visibility}>
                        {att.isSensitive && (
                            <SensitiveInfoTooltip profileId={ownerProfileId}>
                                <small>
                                    <FontAwesomeIcon icon={faLock} />
                                    <Trans>Sensitive</Trans>
                                </small>
                            </SensitiveInfoTooltip>
                        )}

                        {att.hideFromBuyer && (
                            <small>
                                <FontAwesomeIcon icon={faEyeSlash} />
                                <Trans>Hidden from buyer</Trans>
                            </small>
                        )}
                    </div>
                )} */}

                {showVisibilityFlags && (
                    <div className={theme.visibility}>
                        {isEditMode('inline') ? (
                            <div className={theme.checkboxRow}>
                                <Checkbox
                                    inputSize="sm"
                                    checked={att.hideFromBuyer || false}
                                    onChange={(e) => handleChange({ hideFromBuyer: e.target.checked })}>
                                    <Trans>Hide from buyer</Trans>
                                </Checkbox>

                                <SensitiveInfoCheckbox
                                    ownerProfileId={ownerProfileId!}
                                    inputSize="sm"
                                    checked={att.isSensitive || false}
                                    onChange={(flag) => handleChange({ isSensitive: flag })}>
                                    <Trans>Sensitive</Trans>
                                </SensitiveInfoCheckbox>
                            </div>
                        ) : (
                            <>
                                {att.hideFromBuyer && (
                                    <small>
                                        <FontAwesomeIcon icon={faEyeSlash} />
                                        <Trans>Hidden from buyer</Trans>
                                    </small>
                                )}

                                {att.isSensitive && (
                                    <SensitiveInfoTooltip profileId={ownerProfileId}>
                                        <small>
                                            <FontAwesomeIcon icon={faLock} />
                                            <Trans>Sensitive</Trans>
                                        </small>
                                    </SensitiveInfoTooltip>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
            {showContextMenu && (
                <div className={theme.actions}>
                    <DropdownContextMenu>
                        {dropdownItemsVisibility.preview && (
                            <Dropdown.Item as={FileDataPreviewLink} value={att.fileData!} icon={faDownload}>
                                <Trans>Preview</Trans>
                            </Dropdown.Item>
                        )}

                        {dropdownItemsVisibility.download && (
                            <Dropdown.Item as="a" href={downloadUrl} target="_blank" icon={faDownload}>
                                <Trans>Download</Trans>
                            </Dropdown.Item>
                        )}
                        {dropdownItemsVisibility.open && (
                            <Dropdown.Item as="a" href={openUrl} target="_blank" icon={faExternalLink}>
                                <Trans>Open</Trans>
                            </Dropdown.Item>
                        )}

                        {dropdownItemsVisibility.hideFromBuyer && (
                            <Dropdown.Item
                                icon={att.hideFromBuyer ? faEye : faEyeSlash}
                                onClick={(e) => {
                                    handleChange({ hideFromBuyer: !att.hideFromBuyer });
                                }}>
                                {att.hideFromBuyer ? (
                                    <Trans>Show document to buyer</Trans>
                                ) : (
                                    <Trans>Hide document from buyer</Trans>
                                )}
                            </Dropdown.Item>
                        )}

                        {dropdownItemsVisibility.delete && (
                            <Dropdown.Item
                                onClick={() => {
                                    handleDelete();
                                }}
                                icon={faTrashAlt}>
                                <Trans>Delete</Trans>
                            </Dropdown.Item>
                        )}
                    </DropdownContextMenu>
                </div>
            )}
        </>
    );
};

export default AttachmentListItem;
